/**
 * Klaviyo "Added to Cart" tracking.
 *
 * Replaces the legacy snippet that was duplicated across all 10 product
 * templates. That snippet fired on button click rather than on a successful
 * add, described the product of the page rather than the item added, and only
 * patched window.fetch — so gift-with-purchase and add-on bundle adds (jQuery
 * XHR) produced no event at all.
 *
 * This module fires only when Shopify confirms a cart mutation, and builds the
 * payload from the /cart/add.js response body — the only source that knows what
 * was actually added.
 *
 * Payload contract (all money is in dollars, never cents):
 *   Price         unit price actually charged, after any discount
 *   OriginalPrice unit price before discount — informational, for strikethrough
 *                 pricing in emails. Never feeds Value or $value.
 *   Quantity      how many were added by THIS action
 *   Value     total value of everything added in this one action
 *   $value    identical to Value — one value, one meaning
 *   items     post-add cart contents, read fresh after the add resolves
 *
 * The quantity added comes from the request body, not the response: Shopify
 * answers an add with the resulting cart line, so adding one unit to a line
 * that already held three comes back as quantity 4. Reading it from the
 * response is how $value silently became the cart total.
 *
 * NOTE: if Klaviyo's own app embed also has native add-to-cart tracking
 * enabled, events will be counted twice. Verify in the Klaviyo dashboard.
 */
(function () {
  'use strict';

  if (window.__klaviyoAtcInstalled) return;
  window.__klaviyoAtcInstalled = true;

  var METRIC = 'Added to Cart';

  // A real second add of the same variant within this window is almost always a
  // double-submit, not intent. Also guards against a request being seen twice.
  var DEDUPE_WINDOW_MS = 2000;

  // Never let a slow cart read hold the event hostage. If the shopper navigates
  // or the page reloads (the theme does this when the cart is set to
  // "compatible" mode) the event would be lost entirely.
  var CART_READ_TIMEOUT_MS = 800;

  var originalFetch = window.fetch;
  var recent = [];

  function noop() {}

  function pathOf(url) {
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (e) {
      return String(url || '').split('?')[0];
    }
  }

  // Matches /cart/add.js, /cart/add, and locale-prefixed variants such as
  // /en-ca/cart/add.js. The old snippet tested this the wrong way round —
  // pattern.includes(responseUrl) — which let unrelated URLs through.
  function isAddToCartUrl(url) {
    return url ? /(^|\/)cart\/add(\.js)?$/.test(pathOf(url)) : false;
  }

  // Keeps any locale prefix the add request used.
  function cartUrlFor(addUrl) {
    return pathOf(addUrl).replace(/cart\/add(\.js)?$/, 'cart.js');
  }

  function toMoney(cents) {
    return Math.round(Number(cents) || 0) / 100;
  }

  function absoluteUrl(url) {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).href;
    } catch (e) {
      return String(url);
    }
  }

  function imageUrlOf(line) {
    var img = line.image || (line.featured_image && line.featured_image.url) || '';
    if (!img) return '';
    return img.indexOf('//') === 0 ? 'https:' + img : absoluteUrl(img);
  }

  function decodePairs(text) {
    return text.split('&').filter(Boolean).map(function (pair) {
      var split = pair.indexOf('=');
      var key = split === -1 ? pair : pair.slice(0, split);
      var value = split === -1 ? '' : pair.slice(split + 1);
      return [
        decodeURIComponent(key.replace(/\+/g, ' ')),
        decodeURIComponent(value.replace(/\+/g, ' '))
      ];
    });
  }

  function collectJson(json, into) {
    var list = Array.isArray(json.items) ? json.items : (json.id ? [json] : []);
    list.forEach(function (entry) {
      if (entry && entry.id) into[String(entry.id)] = Number(entry.quantity || 1);
    });
  }

  /**
   * Variant id -> quantity requested by this add. Covers the three shapes this
   * theme produces: form-encoded "id=..&quantity=..", jQuery's bracket notation
   * "items[0][id]=..", and a JSON body.
   */
  function requestedQuantities(body) {
    var map = {};
    if (!body) return map;

    if (typeof body === 'string' && body.charAt(0) === '{') {
      try {
        collectJson(JSON.parse(body), map);
        return map;
      } catch (e) {
        // not JSON after all — fall through to the pair parsers
      }
    }

    var pairs = [];
    if (typeof body === 'string') {
      pairs = decodePairs(body);
    } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
      body.forEach(function (value, key) { pairs.push([key, value]); });
    } else if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      body.forEach(function (value, key) { pairs.push([key, value]); });
    } else {
      return map;
    }

    var indexed = {};
    var flatId = null;
    var flatQuantity = null;

    pairs.forEach(function (pair) {
      var bracket = pair[0].match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (bracket) {
        indexed[bracket[1]] = indexed[bracket[1]] || {};
        indexed[bracket[1]][bracket[2]] = pair[1];
      } else if (pair[0] === 'id') {
        flatId = pair[1];
      } else if (pair[0] === 'quantity') {
        flatQuantity = pair[1];
      }
    });

    Object.keys(indexed).forEach(function (i) {
      if (indexed[i].id) map[String(indexed[i].id)] = Number(indexed[i].quantity || 1);
    });
    if (flatId) map[String(flatId)] = Number(flatQuantity || 1);

    return map;
  }

  // /cart/add.js returns a bare line item for a single add, and { items: [...] }
  // when posted an items array. Both forms are used by this theme.
  function linesFrom(data) {
    if (!data) return [];
    if (Array.isArray(data.items)) return data.items;
    if (data.variant_id || data.id) return [data];
    return [];
  }

  // addedQuantity is passed for the lines this action added, and omitted for
  // cart contents, where the line's own quantity and totals are what we want.
  function lineToItem(line, addedQuantity) {
    var quantity = addedQuantity != null ? addedQuantity : (line.quantity || 1);
    var unit = line.final_price != null ? line.final_price : line.price;
    var original = line.original_price != null
      ? line.original_price
      : (line.price != null ? line.price : unit);
    var total = addedQuantity != null
      ? (unit || 0) * addedQuantity
      : (line.final_line_price != null
          ? line.final_line_price
          : (line.line_price != null ? line.line_price : (unit || 0) * quantity));

    return {
      Name: line.product_title || line.title || '',
      ProductID: line.product_id || null,
      VariantID: line.variant_id || line.id || null,
      VariantName: line.variant_title || '',
      SKU: line.sku || '',
      Quantity: quantity,
      Price: toMoney(unit),
      OriginalPrice: toMoney(original),
      LineTotal: toMoney(total),
      URL: absoluteUrl(line.url),
      ImageURL: imageUrlOf(line)
    };
  }

  function fingerprint(items) {
    return items
      .map(function (item) {
        return item.VariantID + 'x' + item.Quantity;
      })
      .sort()
      .join('|');
  }

  function seenRecently(print) {
    var now = Date.now();
    recent = recent.filter(function (entry) {
      return now - entry.at < DEDUPE_WINDOW_MS;
    });
    for (var i = 0; i < recent.length; i++) {
      if (recent[i].print === print) return true;
    }
    recent.push({ print: print, at: now });
    return false;
  }

  // Read via the original fetch so we never re-enter our own hook, and with
  // cache disabled — the legacy snippet's snapshot lagged the cart by exactly
  // one add.
  function readCart(url) {
    if (typeof originalFetch !== 'function') return Promise.resolve(null);
    return originalFetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 't=' + Date.now(), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        return response && response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      });
  }

  function withTimeout(promise, ms) {
    return new Promise(function (resolve) {
      var settled = false;
      function finish(value) {
        if (settled) return;
        settled = true;
        resolve(value);
      }
      setTimeout(function () {
        finish(null);
      }, ms);
      promise.then(finish, function () {
        finish(null);
      });
    });
  }

  function push(payload) {
    window._learnq = window._learnq || [];
    window._learnq.push(['track', METRIC, payload]);
  }

  function track(addUrl, requestBody, data) {
    var lines = linesFrom(data);
    if (!lines.length) return;

    var requested = requestedQuantities(requestBody);
    var added = lines.map(function (line) {
      // Default to one: under-reporting a bulk add is far safer than the old
      // behaviour of reporting the whole cart line as newly added.
      return lineToItem(line, requested[String(line.variant_id || line.id)] || 1);
    });

    if (seenRecently(fingerprint(added))) return;
    var value = Math.round(
      added.reduce(function (sum, item) {
        return sum + item.LineTotal;
      }, 0) * 100
    ) / 100;

    // Never emit a zero-value event.
    if (!(value > 0)) return;

    // Top-level describes the highest-value line added. Deterministic, and the
    // right hero product when an add-on bundle adds several items at once.
    var hero = added.slice().sort(function (a, b) {
      return b.LineTotal - a.LineTotal;
    })[0];

    withTimeout(readCart(cartUrlFor(addUrl)), CART_READ_TIMEOUT_MS).then(function (cart) {
      var payload = {
        Name: hero.Name,
        ProductID: hero.ProductID,
        VariantID: hero.VariantID,
        VariantName: hero.VariantName,
        SKU: hero.SKU,
        Quantity: hero.Quantity,
        URL: hero.URL,
        ImageURL: hero.ImageURL,
        Price: hero.Price,
        OriginalPrice: hero.OriginalPrice,
        Value: value,
        $value: value,
        AddedItems: added,
        AddedItemCount: added.length
      };

      if (cart) {
        payload.total_price = toMoney(cart.total_price);
        payload.original_total_price = toMoney(cart.original_total_price);
        payload.total_discount = toMoney(cart.total_discount);
        payload.item_count = cart.item_count;
        payload.items = (cart.items || []).map(lineToItem);
      }

      push(payload);
    });
  }

  if (typeof originalFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url;
      var request = originalFetch.apply(this, arguments);

      if (isAddToCartUrl(url)) {
        request
          .then(function (response) {
            if (!response || !response.ok) return;
            response
              .clone()
              .json()
              .then(function (data) {
                track(url, init && init.body, data);
              })
              .catch(noop);
          })
          .catch(noop);
      }

      return request;
    };
  }

  // The theme adds the gift-with-purchase product and the add-on bundle through
  // jQuery, which uses XMLHttpRequest. Those adds were invisible before.
  if (window.XMLHttpRequest && window.XMLHttpRequest.prototype) {
    var proto = window.XMLHttpRequest.prototype;
    var originalOpen = proto.open;
    var originalSend = proto.send;

    proto.open = function (method, url) {
      this.__klaviyoAtcUrl = url;
      return originalOpen.apply(this, arguments);
    };

    proto.send = function (body) {
      var xhr = this;

      if (isAddToCartUrl(xhr.__klaviyoAtcUrl)) {
        // readystatechange rather than load: the gift-with-purchase add is a
        // synchronous request, and progress events are skipped for those.
        xhr.addEventListener('readystatechange', function () {
          if (xhr.readyState !== 4) return;
          if (xhr.status < 200 || xhr.status >= 300) return;

          var data;
          try {
            data = JSON.parse(xhr.responseText);
          } catch (e) {
            return;
          }
          track(xhr.__klaviyoAtcUrl, body, data);
        });
      }

      return originalSend.apply(this, arguments);
    };
  }
})();
